import { useLazyQuery } from '@apollo/client';
import { GET_EVALUATION_IDEAS, Idea } from '@frontend/data-access';
import { Empty, List, Spin } from 'antd';
import QueueAnim from 'rc-queue-anim';
import { useEffect, useState } from 'react';
import { selectChannelFilters } from '../channel-store/selectors';
import { useAppSelector } from '../channel-store/settings/hooks';
import IdeaEvaluationDetail from '../evaluate-ideas-pending/detail';
import IdeaEvaluate from '../idea-evaluate/idea-evaluate';
import useTranslation from 'next-translate/useTranslation';

/* eslint-disable-next-line */
export interface EvaluateIdeasCompletedProps {}

const DEFAULT_LIMIT = 10;

export function EvaluateIdeasCompleted({ total }: { total: number }) {
  const { t } = useTranslation('common');
  const channelFilters = useAppSelector(selectChannelFilters);
  const [page, setPage] = useState(1);
  const [getData, { data, loading }] = useLazyQuery<{
    listEvaluationIdeas: {
      items: Idea[];
      total: number;
    };
  }>(GET_EVALUATION_IDEAS, {
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    setPage(1);
  }, [channelFilters]);

  useEffect(() => {
    getData({
      variables: {
        filterBy: {
          ...channelFilters,
        },
        isEvaluated: true,
        page: page,
        limit: DEFAULT_LIMIT,
      },
    });
  }, [channelFilters, getData, page, total]);

  if (loading) {
    return (
      <div className="p-5 text-center">
        <Spin />
      </div>
    );
  }
  if (data?.listEvaluationIdeas.items.length === 0) {
    return <Empty description={t('no_completed_ideas')} />;
  }
  return (
    <div className="">
      <List
        dataSource={data?.listEvaluationIdeas.items}
        split={false}
        pagination={{
          defaultCurrent: 1,
          current: page,
          pageSize: DEFAULT_LIMIT,
          total: data?.listEvaluationIdeas.total || 0,
          onChange: (page) => setPage(page),
          showSizeChanger: false,
        }}
        renderItem={(item) => (
          <QueueAnim>
            <List.Item
              key={item.id}
              className="bg-gray-100 !p-3 border border-gray-400 rounded-lg mb-2 !grid !grid-cols-6 gap-5 divide-x-2 !items-start"
            >
              <div className="col-span-12 lg:col-span-4">
                <IdeaEvaluationDetail idea={item} defaultCollapsed={true} />
              </div>
              <div className="col-span-12 lg:col-span-2 lg:pl-5">
                <IdeaEvaluate ideaId={item.id} />
              </div>
            </List.Item>
          </QueueAnim>
        )}
      />
    </div>
  );
}

export default EvaluateIdeasCompleted;
